// WorkspaceAddPanel: the picker for
// what to lay on the canvas, floating over it (and the empty canvas's start
// screen). One search, one switch between where sessions come from, a strip
// of when the listed ones happened (a bar narrows the list to its stretch),
// and a row per session made for choosing: what it was, when it ran within the
// range, where, how long, how big; for a flagged one, how bad its findings are
// and how triage judged them. A pick lays the session on and brings its card
// into view; picking one already on only brings it into view.
import { useCallback, useEffect, useMemo, useState } from "react";
import QueryBar from "./QueryBar.jsx";
import HoverTooltip from "./HoverTooltip.jsx";
import { MachineName, SEVERITIES, useChatSessions, useFindingSessions, useTitles } from "./WorkspaceSidebar.jsx";
import { suggestValues } from "../lib/queryLangSuggest.js";
import { compilePipeline, looksLikePipeline, pipelineFilterText } from "../lib/queryPipeline.js";
import { SESSION_MATCH_CAP, kernelOverSessions } from "../lib/sessionMatch.js";
import { PROVIDER_TAG_TO_IDENTITY } from "../lib/agentIdentity.js";
import KernelWidget from "./KernelWidget.jsx";
import { rangeBoundsMs } from "../lib/analyticsClient.js";
import { toApiRange } from "../lib/shell.js";
import { DATE_RANGE_PRESETS } from "../lib/dateRangePresets.js";
import { LITE_VERDICT_LABEL } from "../lib/triageLite.js";
import { useFleetFindings } from "../lib/fleetFindings.js";
import { useKeyedReads } from "../lib/keyedReads.js";
import { SEVERITY_COLOR, behaviourTone } from "../lib/traceReview.js";
import { SeverityChip } from "./lenses/triageParts.jsx";
import { addToWorkspace, requestWorkspaceFocus, useWorkspace, useWorkspaceCanvas } from "../lib/workspaceStore.js";
import { useSessionMatch } from "../lib/workspaceSearch.js";
import { holdsText } from "../lib/traceCanvasStore.js";
import { setDragItem } from "../lib/dragTypes.js";

const BUCKETS = 48;
const PAGE = 80;
const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const DOT = " \u00b7 ";
// Severity in the review's own words and colours, stacked worst first.
const SEV_LABEL = { critical: "Critical", high: "High", medium: "Medium", low: "Low", other: "Other" };
const sevColor = (sev) => (sev === "other" ? "var(--muted-soft)" : SEVERITY_COLOR[behaviourTone(sev)]);
export const AGENT_HUES = ["#c9a45f", "#7fa3c9", "#8fb376", "#a98fc9", "#c98383", "#7bbfae"];

// Made once: a list says when for every row.
const ON_DATE = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
const AT_TIME = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
export const whenText = (ms) => (Number.isFinite(ms) ? `${ON_DATE.format(ms)} ${AT_TIME.format(ms)}` : "");
export const lastingText = (ms) => (!(ms > 0) ? "" : ms < HOUR ? `${Math.max(1, Math.round(ms / MINUTE))}m` : ms < 2 * DAY ? `${(ms / HOUR).toFixed(ms < 10 * HOUR ? 1 : 0).replace(/\.0$/, "")}h` : `${Math.round(ms / DAY)}d`);
export const countText = (n) => (n >= 10_000 ? `${(n / 1000).toFixed(0)}K` : n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K` : String(n));
const shortId = (id) => `session ${String(id).slice(0, 8)}`;
export const words = (q) => String(q || "").toLowerCase().split(/\s+/).filter((w) => w && !w.includes(":"));
export const matches = (text, ws) => ws.every((w) => String(text || "").toLowerCase().includes(w));

// Items bucketed over [from, to], stacked by series, for the strip.
export function bucketize(items, { from, to, seriesOf, timeOf }) {
  const span = Math.max(1, to - from);
  const out = Array.from({ length: BUCKETS }, () => new Map());
  for (const it of items) {
    const t = timeOf(it);
    if (!Number.isFinite(t) || t < from || t > to) continue;
    const b = Math.min(BUCKETS - 1, Math.floor(((t - from) / span) * BUCKETS));
    const s = seriesOf(it);
    out[b].set(s, (out[b].get(s) || 0) + 1);
  }
  return out;
}

// When the listed sessions happened, a bar per stretch stacked by series; a
// click narrows the list to a stretch, a second click widens it again. With
// `cap` (a share of the stretches, 0.9), a stretch far above the rest does not
// set the scale: the scale stops where that share of the stretches do, and a
// taller one reaches the top, cut, its count still on hover. As many
// stretches as `buckets` holds.
// Its legend can stand for what each series is: `onSeriesPick(id)` hears one
// clicked and `onSeriesPoint(id)` one pointed at (a series with `pick: false`
// stands for many things, and is neither); `lit` brings one series forward,
// the rest fading back, and `held(id)` says one is what the search is
// narrowed to. A series' `count` is what its legend says, in place of its
// bars' sum (a list's own count of it).
const quantile = (values, q) => {
  const v = [...values].sort((a, b) => a - b);
  return v.length ? v[Math.min(v.length - 1, Math.floor(q * (v.length - 1)))] : 0;
};
export function ActivityStrip({ buckets, series, from, to, picked, onPick, cap = null, lit = null, onSeriesPoint = null, onSeriesPick = null, held = null }) {
  const [hover, setHover] = useState(null);
  const n = buckets.length || BUCKETS;
  const sums = buckets.map((b) => [...b.values()].reduce((a, v) => a + v, 0));
  const top = Math.max(1, ...sums);
  const q = cap ? quantile(sums.filter(Boolean), cap) : 0;
  const max = q > 0 && top > 2 * q ? q : top;
  const span = (to - from) / n;
  const at = (i) => ({ t0: from + i * span, t1: from + (i + 1) * span });
  const totals = new Map();
  for (const b of buckets) for (const [k, v] of b) totals.set(k, (totals.get(k) || 0) + v);
  for (const s of series) if (Number.isFinite(s.count)) totals.set(s.id, s.count);
  const hoverTotal = hover != null ? [...buckets[hover].values()].reduce((a, v) => a + v, 0) : 0;
  return (
    <div className="shrink-0 px-[16px] pt-[10px]" data-workspace-strip="">
      <svg viewBox={`0 0 ${n * 10} 40`} preserveAspectRatio="none" className="block h-[40px] w-full" onMouseLeave={() => setHover(null)}>
        {buckets.map((b, i) => {
          let y = 40;
          const total = sums[i];
          const cut = total > max;
          const k = 36 / (cut ? total : max);
          const on = picked && Math.abs(at(i).t0 - picked.t0) < 1;
          return (
            <g key={i} onMouseEnter={() => setHover(i)} onClick={() => onPick(on ? null : at(i))} className="cursor-pointer">
              <rect x={i * 10} y="0" width="10" height="40" fill={on || hover === i ? "var(--gray-fill)" : "transparent"} />
              {series.map((s) => {
                const v = b.get(s.id) || 0;
                if (!v) return null;
                const h = Math.max(1.5, v * k);
                y -= h;
                return <rect key={s.id} x={i * 10 + 1.5} y={y} width="7" height={h} fill={s.color} opacity={picked && !on ? 0.35 : lit != null && s.id !== lit ? 0.3 : 0.85} />;
              })}
              {!total ? <rect x={i * 10 + 1.5} y="39" width="7" height="1" fill="var(--line)" /> : null}
              {cut ? <rect x={i * 10 + 1.5} y="9" width="7" height="1.5" fill="var(--workspace-bg)" /> : null}
            </g>
          );
        })}
      </svg>
      <div className="flex min-h-[18px] flex-wrap items-center gap-x-[12px] pt-[3px] text-[11px] text-[color:var(--muted)]">
        {series.filter((s) => totals.get(s.id)).map((s) => {
          const said = (
            <>
              <span className="h-[7px] w-[7px] shrink-0 rounded-[1px]" style={{ background: s.color }} />
              <span className="max-w-[180px] truncate">{s.label}</span>
              <span className="tabular-nums text-[color:var(--muted-soft)]">{countText(totals.get(s.id))}</span>
            </>
          );
          if (!onSeriesPick || s.pick === false) return <span key={s.id} className="inline-flex min-w-0 items-center gap-[5px]" data-strip-series={s.id}>{said}</span>;
          const on = !!held?.(s.id);
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onSeriesPick(s.id)}
              onMouseEnter={() => onSeriesPoint?.(s.id)}
              onMouseLeave={() => onSeriesPoint?.(null)}
              onFocus={() => onSeriesPoint?.(s.id)}
              onBlur={() => onSeriesPoint?.(null)}
              aria-pressed={on}
              className={`inline-flex min-w-0 cursor-pointer items-center gap-[5px] transition-colors hover:text-[color:var(--ink)] ${on || lit === s.id ? "text-[color:var(--ink)]" : ""}`}
              data-strip-series={s.id}
            >
              {said}
            </button>
          );
        })}
        <span className="ml-auto tabular-nums">
          {hover != null ? `${whenText(at(hover).t0)} \u2013 ${whenText(at(hover).t1)}${DOT}${hoverTotal}`
            : picked ? (
              <button type="button" onClick={() => onPick(null)} className="text-[color:var(--ink)] hover:text-[color:var(--green)]">
                {`${whenText(picked.t0)} \u2013 ${whenText(picked.t1)} \u00d7`}
              </button>
            ) : `${whenText(from)} \u2013 ${whenText(to)}`}
        </span>
      </div>
    </div>
  );
}

// Where a session ran within the range, as a mark on a thin track.
function SpanBar({ first, last, from, to }) {
  if (!Number.isFinite(last)) return <span className="w-[96px]" />;
  const span = Math.max(1, to - from);
  const a = Math.max(0, Math.min(1, ((Number.isFinite(first) ? first : last) - from) / span));
  const b = Math.max(a, Math.min(1, (last - from) / span));
  return (
    <span className="relative block h-[6px] w-[96px] shrink-0 rounded-[1px] bg-[color:var(--gray-fill)]" aria-hidden="true">
      <span className="absolute top-0 h-full rounded-[1px] bg-[color:var(--muted)]" style={{ left: `${a * 100}%`, width: `max(2px, ${(b - a) * 100}%)` }} />
    </span>
  );
}

// One session to choose: on the canvas or not, what it was, where it ran,
// and a second line the source chooses. A matched session's third says how
// much of it each clause of the query found. Clicked, it is laid on; dragged,
// it goes wherever it is dropped.
function Row({ id, title, lead = null, detail, hits = "", endpointId, first, last, from, to, on, pick }) {
  return (
    <button
      type="button"
      draggable
      onDragStart={(e) => setDragItem(e, { kind: "session", id: String(id).toLowerCase(), title })}
      onClick={() => pick(id, title)}
      aria-pressed={on}
      className="relative grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-x-[16px] px-[16px] py-[8px] text-left hover:bg-[color:var(--gray-fill)]"
      data-workspace-add-row=""
    >
      <span className={`absolute left-0 top-[8px] w-[16px] text-center text-[12px] leading-[16px] ${on ? "text-[color:var(--green)]" : "text-[color:var(--muted-soft)]"}`} aria-hidden="true">{on ? "\u2713" : "+"}</span>
      <span className="min-w-0">
        <span className="flex min-w-0 items-baseline gap-[6px]">
          {lead}
          <span className="min-w-0 truncate text-[12.5px] leading-[16px] text-[color:var(--ink)]">{title}</span>
        </span>
        <span className="mt-[2px] block truncate text-[11px] leading-[14px] text-[color:var(--muted)]">{detail}</span>
        {hits ? <span className="mt-[2px] block truncate text-[11px] leading-[14px] text-[color:var(--muted-soft)]" data-workspace-add-hits="">{hits}</span> : null}
      </span>
      <span className="flex flex-col items-end">
        <span className="h-[16px] max-w-[160px] truncate text-[11px] leading-[16px] text-[color:var(--muted)]">{endpointId ? <MachineName id={endpointId} /> : null}</span>
        <span className="mt-[6px]"><SpanBar first={first} last={last} from={from} to={to} /></span>
      </span>
    </button>
  );
}

function Quiet({ children }) {
  return <p className="px-[16px] py-[16px] text-[12px] leading-[1.45] text-[color:var(--muted)]">{children}</p>;
}

// The flagged sessions of the range, each with its findings (how bad the
// worst is, what they are) and how the triage runs judged them: from the
// range's findings and verdicts as the fleet reads them, shared with every
// other reader of them.
export function useFlagged(range) {
  const found = useFindingSessions(range);
  const fleet = useFleetFindings(range);
  return useMemo(() => {
    const bySession = new Map();
    for (const f of fleet.findings) {
      if (!f.session) continue;
      if (!bySession.has(f.session)) bySession.set(f.session, []);
      bySession.get(f.session).push(f);
    }
    const rank = new Map(SEVERITIES.map(([id], i) => [id, i]));
    const sessions = found.sessions.map((s) => {
      const rows = bySession.get(s.id) || [];
      const judged = {};
      for (const f of rows) if (f.verdict) judged[f.verdict] = (judged[f.verdict] || 0) + 1;
      const titles = [...rows]
        .sort((a, b) => (rank.get(a.severity) ?? 9) - (rank.get(b.severity) ?? 9))
        .map((f) => f.title).filter(Boolean);
      const sev = rank.has(s.worstBehaviour) ? s.worstBehaviour : "other";
      return { ...s, sev, titles: [...new Set(titles)], judged };
    }).sort((a, b) => rank.get(a.sev) - rank.get(b.sev) || (b.latestAt || 0) - (a.latestAt || 0));
    return { status: found.status, sessions };
  }, [found.status, found.sessions, fleet.findings]);
}

const NO_SESSIONS = [];

// An agent by the provider tag its session's requests carry.
const agentName = (tag) => PROVIDER_TAG_TO_IDENTITY[tag]?.name || tag || "Other";

// How much of a matched session each counted clause found in it.
export function hitsText(session, clauses) {
  const parts = [];
  clauses.forEach((c, i) => {
    const n = session.hits?.[i];
    if (c.negated || c.grain !== "count" || !n) return;
    parts.push(`${c.label} in ${countText(n)} request${n === 1 ? "" : "s"}`);
  });
  return parts.join(DOT);
}

// What the query means of a session, said once it has more than one clause or
// a negated one.
export function matchNote(clauses) {
  const positive = clauses.filter((c) => c.grain === "count" && !c.negated).length;
  const negated = clauses.some((c) => c.grain === "count" && c.negated);
  if (positive > 1 && negated) return "Each clause can hold anywhere in a session; a negated one, nowhere in it.";
  if (positive > 1) return "Each clause can hold anywhere in a session, not only in one request.";
  if (negated) return "A negated clause holds when nothing in the session matches it.";
  return "";
}

// `canvases`: the workspaces kept in this browser, the one open
// `activeCanvas`, offered as a source of their own. `extraSources`: sources
// of the host's own, listed first ({ needle, on } to [{ id, label, count,
// body }]), `initialSource` the one it opens on (`onSourceChange` hears the
// reader turn to another); `hideSources` the panel's own it leaves out.
// `title` and `subtitle` say what a pick does there. `onKeepSearch` keeps the
// search written (its text) on the workspace as a card of what it matches;
// `onNote` puts a note of the reader's own on it. `collect`: there is no
// workspace yet, and what is picked goes into the search one starts from
// (`traces` the sessions in it), a session clicked again coming out.
export default function WorkspaceAddPanel({
  range, onRangeChange = null, onClose = null, floating = false, traces = null, anchor = null, onLay = null, chat: withChat = true, canvases = null, activeCanvas = null, onOpenCanvas = null,
  extraSources = null, initialSource = "sessions", onSourceChange = null, hideSources = NO_SESSIONS, title = null, subtitle = null, onKeepSearch = null, onNote = null,
  onNestCanvas = null, collect = false,
}) {
  const [source, setSource] = useState(initialSource);
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [limit, setLimit] = useState(PAGE);
  const ws = useWorkspace();
  const canvas = useWorkspaceCanvas();
  const chatSessions = useChatSessions();
  const chat = withChat ? chatSessions : NO_SESSIONS;
  const onBoard = traces ?? ws.traces;
  const anchorId = traces ? anchor : ws.anchor;
  const on = useMemo(() => new Set(onBoard.map((t) => t.id)), [onBoard]);
  const titled = useMemo(() => new Map(onBoard.filter((t) => t.title).map((t) => [t.id, t.title])), [onBoard]);
  // A `|>` query is an aggregation: its filter picks the sessions, and its
  // aggregate is drawn over the whole of each.
  const [pipe, setPipe] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(pipelineFilterText(text));
      setPipe(looksLikePipeline(text) ? compilePipeline(text, { scopeMode: "scoped" }) : null);
    }, 400);
    return () => clearTimeout(t);
  }, [text]);
  const rangeKey = typeof range === "string" ? range : JSON.stringify(range ?? null);
  useEffect(() => { setPicked(null); setLimit(PAGE); }, [source, q, rangeKey]);
  useEffect(() => {
    if (!onClose) return undefined;
    // An Esc the query bar took (closing its suggestions) is not for the sheet.
    const onKey = (e) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      e.preventDefault();
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const bounds = useMemo(() => {
    const { fromMs, toMs } = rangeBoundsMs(typeof range === "string" ? toApiRange(range) : range);
    return { from: fromMs, to: Math.min(toMs, Date.now()) };
  }, [rangeKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = useCallback((id, title) => {
    const key = String(id || "").toLowerCase();
    if (!key) return;
    if (collect) {
      onLay?.({ id: key, title: title || "" });
      return;
    }
    if (!on.has(key)) (onLay || addToWorkspace)({ id: key, title: title || "" });
    requestWorkspaceFocus(key);
  }, [on, onLay, collect]);
  const suggest = useCallback((field, term) => suggestValues(field, term, { scopePath: [], range }), [rangeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sessions: the query language over every session in the range, each clause
  // asked of the whole session (lib/sessionMatch.js), so the session is what
  // is found, counted and drawn. The strip is when the matched sessions were
  // last active; a bar narrows the list to its stretch.
  const match = useSessionMatch(range, q, { enabled: source === "sessions" || !!pipe?.kernel });
  const matched = match.data?.sessions || NO_SESSIONS;
  const clauses = match.data?.clauses || NO_SESSIONS;
  const listed = useMemo(() => (picked ? matched.filter((s) => s.lastMs >= picked.t0 && s.lastMs < picked.t1) : matched), [matched, picked]);
  const page = useMemo(() => listed.slice(0, limit), [listed, limit]);
  // Shared with the sidebar's rows; a page shown more of reads only its new
  // sessions.
  const pageIds = useMemo(() => (source === "sessions" ? page.map((s) => s.id) : NO_SESSIONS), [source, page]);
  const heads = useKeyedReads("getSessionHeads", pageIds, { range, whole: !!match.data?.prefiltered });
  const flagged = useFlagged(range);
  // What each flagged session was about, read for the list while it is open.
  const flaggedIds = useMemo(() => (source === "flagged" ? flagged.sessions.filter((s) => !s.summary).map((s) => s.id) : NO_SESSIONS), [source, flagged.sessions]);
  const flaggedHeads = useKeyedReads("getSessionHeads", flaggedIds, { range });
  const flaggedTitle = (s) => s.summary || flaggedHeads.data?.[s.id]?.summary || "";
  const related = useMemo(() => (canvas?.related || []).filter((r) => !r.kgOnly).sort((a, b) => b.evidence - a.evidence || b.shared - a.shared).slice(0, 80), [canvas?.related]);
  const relatedSpan = useMemo(() => {
    const ts = related.flatMap((r) => [r.firstMs, r.lastMs]).filter(Number.isFinite);
    return ts.length ? { from: Math.min(...ts), to: Math.max(...ts) } : null;
  }, [related]);
  const titles = useTitles([...related.map((r) => r.id), ...chat.map((c) => String(c.id).toLowerCase())].filter((id) => !titled.has(id)), relatedSpan);
  const titleOf = (id) => titled.get(id) || titles.get(id) || shortId(id);
  const needle = words(q);

  const extra = extraSources ? extraSources({ needle, on }) : NO_SESSIONS;
  const sources = [
    ...extra,
    ...[
      { id: "sessions", label: "Sessions", count: match.data ? `${countText(matched.length)}${match.data.capped ? "+" : ""}` : null },
      { id: "flagged", label: "Flagged", count: flagged.status === "done" ? countText(flagged.sessions.length) : null },
      ...(anchorId ? [{ id: "related", label: "Related", count: canvas?.status === "done" ? countText(related.length) : null }] : []),
      ...(chat.length ? [{ id: "chat", label: "In the chat", count: countText(chat.length) }] : []),
      ...(canvases ? [{ id: "saved", label: "Saved", count: canvases.length ? countText(canvases.length) : null }] : []),
    ].filter((x) => !hideSources.includes(x.id)),
  ];
  const current = sources.some((s) => s.id === source) ? source : "sessions";
  const extraBody = extra.find((x) => x.id === current)?.body || null;

  let strip = null;
  let body = null;
  if (extraBody) {
    body = extraBody;
  } else if (current === "sessions") {
    const agents = [...new Set(matched.map((s) => s.agent))];
    const series = agents.map((a, i) => ({ id: a, label: agentName(a), color: AGENT_HUES[i % AGENT_HUES.length] }));
    strip = <ActivityStrip buckets={bucketize(matched, { from: bounds.from, to: bounds.to, seriesOf: (s) => s.agent, timeOf: (s) => s.lastMs })} series={series} from={bounds.from} to={bounds.to} picked={picked} onPick={setPicked} />;
    body = match.loading && !match.data ? <Quiet>Searching...</Quiet>
      : match.error ? <Quiet>The search failed.</Quiet>
        : !listed.length ? <Quiet>{`No session matches${picked ? " in that stretch" : " in this range"}.`}</Quiet>
          : (
            <>
              {page.map((s) => {
                const head = heads.data?.[s.id] || null;
                const first = head?.firstMs ?? s.firstMs;
                const last = head?.lastMs ?? s.lastMs;
                const requests = head?.requests ?? s.requests;
                const title = head?.summary || titled.get(s.id) || shortId(s.id);
                const detail = [
                  whenText(last),
                  Number.isFinite(first) && Number.isFinite(last) ? `ran ${lastingText(last - first)}` : null,
                  requests ? `${countText(requests)} requests` : null,
                  head?.model || null,
                ].filter(Boolean).join(DOT);
                return <Row key={s.id} id={s.id} title={title} detail={detail} hits={hitsText(s, clauses)} endpointId={head?.endpointId || s.endpointId} first={first} last={last} from={bounds.from} to={bounds.to} on={on.has(s.id)} pick={pick} />;
              })}
              {listed.length > page.length ? (
                <button type="button" onClick={() => setLimit((n) => n + PAGE)} className="w-full px-[16px] py-[10px] text-left text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)]">
                  {`Show more${DOT}${countText(listed.length - page.length)} left`}
                </button>
              ) : null}
              {match.data?.capped ? <Quiet>{`These are the newest ${countText(SESSION_MATCH_CAP)} sessions that match. Narrow the query or the range to reach the rest.`}</Quiet> : null}
            </>
          );
  } else if (current === "flagged") {
    const list = flagged.sessions.filter((s) => matches(`${flaggedTitle(s)} ${s.titles.join(" ")}`, needle));
    const inStretch = picked ? list.filter((s) => s.latestAt >= picked.t0 && s.latestAt < picked.t1) : list;
    const series = SEVERITIES.map(([id]) => ({ id, label: SEV_LABEL[id], color: sevColor(id) }));
    strip = <ActivityStrip buckets={bucketize(list, { from: bounds.from, to: bounds.to, seriesOf: (s) => s.sev, timeOf: (s) => s.latestAt })} series={series} from={bounds.from} to={bounds.to} picked={picked} onPick={setPicked} />;
    body = flagged.status === "loading" ? <Quiet>Reading the reviews...</Quiet>
      : flagged.status === "error" ? <Quiet>The reviews could not be read.</Quiet>
        : !inStretch.length ? <Quiet>{`Nothing flagged${picked ? " in that stretch" : " matches"}.`}</Quiet>
          : inStretch.map((s) => {
            const judged = Object.entries(s.judged).map(([v, k]) => (LITE_VERDICT_LABEL[v] ? `${k} ${LITE_VERDICT_LABEL[v].toLowerCase()}` : null)).filter(Boolean);
            const detail = [
              `${s.findings} finding${s.findings === 1 ? "" : "s"}${s.titles[0] ? `: ${s.titles[0]}` : ""}`,
              judged.length ? `triage ${judged.join(", ")}` : null,
              whenText(s.latestAt),
            ].filter(Boolean).join(DOT);
            return (
              <Row
                key={s.id}
                id={s.id}
                title={flaggedTitle(s) || shortId(s.id)}
                lead={s.sev === "other" ? null : <SeverityChip severity={s.sev} />}
                detail={detail}
                endpointId={s.endpointId}
                first={s.firstAt}
                last={s.latestAt}
                from={bounds.from}
                to={bounds.to}
                on={on.has(s.id)}
                pick={pick}
              />
            );
          });
  } else if (current === "related") {
    const list = related.filter((r) => matches(titleOf(r.id), needle));
    const span = relatedSpan || bounds;
    const inStretch = picked ? list.filter((r) => r.firstMs >= picked.t0 && r.firstMs < picked.t1) : list;
    strip = <ActivityStrip buckets={bucketize(list, { from: span.from, to: span.to, seriesOf: () => "related", timeOf: (r) => r.firstMs })} series={[{ id: "related", label: "shares the anchor's content", color: "var(--join)" }]} from={span.from} to={span.to} picked={picked} onPick={setPicked} />;
    body = !canvas || canvas.status === "loading" || canvas.status === "idle" ? <Quiet>Searching for the sessions the anchor shares content with...</Quiet>
      : canvas.status === "error" ? <Quiet>The search for related sessions failed.</Quiet>
        : !inStretch.length ? <Quiet>{`No other session shares the anchor's content${picked ? " in that stretch" : ""}.`}</Quiet>
          : inStretch.map((r) => (
            <Row
              key={r.id}
              id={r.id}
              title={titleOf(r.id)}
              detail={[r.shared ? `${r.shared} strings shared with the anchor` : null, whenText(r.firstMs), Number.isFinite(r.lastMs) && Number.isFinite(r.firstMs) ? `ran ${lastingText(r.lastMs - r.firstMs)}` : null].filter(Boolean).join(DOT)}
              endpointId={r.endpointId}
              first={r.firstMs}
              last={r.lastMs}
              from={span.from}
              to={span.to}
              on={on.has(r.id)}
              pick={pick}
            />
          ));
  } else if (current === "saved") {
    const list = (canvases || []).filter((c) => matches(c.title || "", needle));
    body = !list.length ? <Quiet>{needle.length ? "No saved workspace matches." : "A workspace is saved as you work in it. Lay a session on to start one."}</Quiet>
      : list.map((c) => (
        <div key={c.id} className={`flex items-stretch hover:bg-[color:var(--gray-fill)] ${c.id === activeCanvas ? "bg-[color:var(--green-soft)]" : ""}`} data-workspace-saved="">
          <button
            type="button"
            draggable
            onDragStart={(e) => setDragItem(e, { kind: "workspace", id: c.id, title: c.title })}
            onClick={() => onOpenCanvas?.(c.id)}
            aria-pressed={c.id === activeCanvas}
            className="grid min-w-0 flex-1 gap-[2px] px-[16px] py-[8px] text-left"
          >
            <span className="truncate text-[12.5px] leading-[16px] text-[color:var(--ink)]">{c.title || (c.anchor ? shortId(c.anchor) : "Untitled workspace")}</span>
            <span className="truncate text-[11px] leading-[14px] text-[color:var(--muted)]">
              {[holdsText(c), c.chat ? "from a chat" : null, c.at ? `opened ${whenText(c.at)}` : null].filter(Boolean).join(DOT)}
            </span>
          </button>
          {onNestCanvas && c.id !== activeCanvas ? (
            <HoverTooltip placement="left" label="Put this workspace inside the one being worked in: a card of it there, gone into by a double-click">
              <button type="button" onClick={() => onNestCanvas(c.id)} className="flex-none px-[12px] text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)]" data-workspace-nest="">Put inside</button>
            </HoverTooltip>
          ) : null}
        </div>
      ));
  } else {
    body = chat.filter((c) => matches(titleOf(String(c.id).toLowerCase()), needle)).map((c) => {
      const id = String(c.id).toLowerCase();
      return (
        <Row
          key={id}
          id={id}
          title={titleOf(id)}
          detail={c.claims ? `${c.claims} claim${c.claims === 1 ? "" : "s"} rest on it in the chat's investigation` : "named in the chat's investigation"}
          from={bounds.from}
          to={bounds.to}
          on={on.has(id)}
          pick={pick}
        />
      );
    });
  }

  // The aggregate is drawn over every request of the matched sessions once
  // the match is in; past the cap, over the requests the query matches, and
  // it says so. With no clause it is the range's, as written.
  const kernel = useMemo(() => {
    if (!pipe?.kernel) return null;
    if (!clauses.length && match.data) return pipe.kernel;
    if (!match.data) return null;
    return match.data.capped ? pipe.kernel : kernelOverSessions(pipe.kernel, matched.map((s) => s.id));
  }, [pipe, match.data, matched, clauses.length]);
  const aggregateNote = pipe?.kernel && match.data?.capped
    ? `Over ${countText(SESSION_MATCH_CAP)} sessions match, so this counts the requests that match rather than whole sessions.`
    : "";
  const aggregate = pipe ? (
    <div className="shrink-0 px-[16px] pt-[12px]" data-workspace-aggregate="">
      {pipe.kernel ? (
        kernel ? (
          <div className="grid gap-[4px]">
            <div className="h-[200px] min-h-0">
              <KernelWidget kernel={kernel} params={{ scope: [], range }} showHeader={false} />
            </div>
            {aggregateNote ? <p className="m-0 text-[11px] leading-[1.4] text-[color:var(--muted)]">{aggregateNote}</p> : null}
          </div>
        ) : <p className="m-0 text-[11px] leading-[1.4] text-[color:var(--muted)]">Finding the sessions to draw it over...</p>
      ) : (
        <p className="m-0 text-[11px] leading-[1.4] text-[color:var(--muted)]">
          {(pipe.errors.length ? pipe.errors.map((e) => e.message) : ["Add an aggregate, such as uniq session by day."]).join(DOT)}
        </p>
      )}
    </div>
  ) : null;
  const note = [matchNote(clauses), ...(match.data?.warnings || []).map((w) => w.message)].filter(Boolean).join(" ");

  const n = onBoard.length;
  return (
    <section className={`flex h-full min-h-0 min-w-0 flex-col overflow-hidden border border-[color:var(--line-strong)] ${floating ? "rounded-[2px] bg-[color:var(--panel)]" : "bg-[color:var(--bg)]"}`} data-workspace-add="">
      <header className="flex shrink-0 items-center gap-[10px] border-b border-[color:var(--line)] px-[16px] py-[10px]">
        <div className="grid min-w-0 flex-1 gap-[2px]">
          <h2 className="m-0 truncate text-[13px] font-medium leading-[1.2] text-[color:var(--ink)]">{title || (onClose ? "Add sessions to the workspace" : "Lay sessions on this workspace")}</h2>
          <span className="truncate text-[11px] text-[color:var(--muted)]">{subtitle || (n ? `${n} on the workspace${DOT}click a session to lay it on` : "Click a session to lay it on the workspace")}</span>
        </div>
        {onClose ? (
          <HoverTooltip placement="bottom" label="Close (Esc)">
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="grid h-[24px] w-[24px] shrink-0 place-items-center border border-[color:var(--line)] bg-[color:var(--control-bg)] p-0 text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] [&_svg]:block [&_svg]:h-3 [&_svg]:w-3"
              data-workspace-add-done=""
            >
              <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><path d="m4 4 8 8M12 4l-8 8" /></svg>
            </button>
          </HoverTooltip>
        ) : null}
      </header>
      <div className="flex shrink-0 items-start gap-[8px] px-[16px] pt-[12px]">
        <div className="min-w-0 flex-1">
          <QueryBar value={text} onChange={setText} suggestValues={suggest} placeholder="Find sessions: tool:Bash file:deploy -model:haiku |> uniq session by day" label={null} />
        </div>
        <span className="inline-flex h-[30px] shrink-0 items-stretch divide-x divide-[color:var(--line)] rounded-[2px] border border-[color:var(--line)] text-[11px]">
          {DATE_RANGE_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onRangeChange?.(p.id)}
              aria-pressed={range === p.id}
              className={`px-[9px] ${range === p.id ? "text-[color:var(--ink)] bg-[color:var(--green-soft)]" : "text-[color:var(--muted)] hover:text-[color:var(--ink)]"}`}
            >
              {p.label}
            </button>
          ))}
        </span>
      </div>
      {onKeepSearch || onNote ? (
        <div className="flex shrink-0 flex-wrap items-center gap-x-[14px] gap-y-[4px] px-[16px] pt-[8px] text-[11px]" data-workspace-keep="">
          {onKeepSearch ? (
            <HoverTooltip placement="bottom" label={collect ? "Narrow the board to what this search matches, to start a workspace from" : "A card of the sessions this search matches, on the workspace to branch out from: a file path (file:), a topic (topic:), anything the search reads"}>
              <button type="button" disabled={!text.trim()} onClick={() => onKeepSearch(text.trim())} className="text-[color:var(--muted)] hover:text-[color:var(--ink)] disabled:opacity-40 disabled:hover:text-[color:var(--muted)]" data-workspace-keep-search="">
                {collect ? "+ Add this search to the board's" : "+ Keep this search on the workspace"}
              </button>
            </HoverTooltip>
          ) : null}
          {onNote ? (
            <button type="button" onClick={onNote} className="text-[color:var(--muted)] hover:text-[color:var(--ink)]" data-workspace-write-note="">+ Write a note on it</button>
          ) : null}
        </div>
      ) : null}
      {note && current === "sessions" ? <p className="m-0 shrink-0 px-[16px] pt-[6px] text-[11px] leading-[1.4] text-[color:var(--muted)]" data-workspace-match-note="">{note}</p> : null}
      {aggregate}
      <div className="flex shrink-0 flex-wrap items-baseline gap-x-[16px] gap-y-[4px] px-[16px] pt-[12px] text-[12px]" role="tablist" aria-label="Where sessions come from">
        {sources.map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={current === s.id}
            onClick={() => { setSource(s.id); onSourceChange?.(s.id); }}
            className={current === s.id ? "font-medium text-[color:var(--ink)]" : "text-[color:var(--muted)] hover:text-[color:var(--ink)]"}
          >
            {s.label}
            {s.count ? <span className="ml-[5px] font-normal tabular-nums text-[color:var(--muted-soft)]">{s.count}</span> : null}
          </button>
        ))}
      </div>
      {strip}
      <div className="mt-[6px] min-h-0 flex-1 overflow-y-auto overscroll-contain border-t border-[color:var(--line-soft)]" data-workspace-add-list="">
        {body}
      </div>
    </section>
  );
}
